import { useMemo } from 'react';

import {
  type Service,
  serviceCatalogue,
  servicePath,
  type ServicePillar,
  servicePillars,
} from '@/content/services';

/** A pillar with the services under it, and the anchor the rail jumps to. */
export type CataloguePillar = ServicePillar & {
  /** `id` is the pillar's own key; this is the DOM id of its band. */
  anchorId: string;
  services: readonly Service[];
};

export type ServiceCatalogue = {
  pillars: readonly CataloguePillar[];
  /** In rail order, for `useScrollSpy`. */
  anchorIds: readonly string[];
};

/** The anchor a pillar's block is reachable at. One place, so the rail and the band agree. */
export const pillarAnchorId = (pillar: Pick<ServicePillar, 'id'>) => `services-${pillar.id}`;

/**
 * The catalogue, grouped into its pillars and given the ids the page needs.
 *
 * The grouping itself is the shared catalogue's (`servicePillars`), not this
 * page's — the header menu groups the services the same way, and a hub that
 * sorted them differently from the menu that opened it would be two answers to
 * one question. What this adds is page-local: an anchor per pillar, and the
 * flat list of those anchors in rail order, which is what the scroll spy wants
 * and what no component should assemble while it is also drawing itself.
 *
 * A pillar with no services is dropped rather than drawn empty. Nothing
 * produces one today — every pillar has at least two — but the catalogue is
 * the file that will be edited, and a heading over nothing is the failure that
 * edit produces.
 */
export function useServiceCatalogue(): ServiceCatalogue {
  return useMemo(() => {
    const pillars = servicePillars
      .map<CataloguePillar>((pillar) => ({
        ...pillar,
        anchorId: pillarAnchorId(pillar),
        services: serviceCatalogue.filter((service) => service.pillar === pillar.id),
      }))
      .filter((pillar) => pillar.services.length > 0);

    return { pillars, anchorIds: pillars.map((pillar) => pillar.anchorId) };
  }, []);
}

/** Where a service's own page lives. Re-exported so bands import one module. */
export { servicePath };
